/* eslint-disable max-len */
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import './BotHistoryChart.scss';
import {
  AreaChart, Area, CartesianGrid, XAxis, YAxis,
  Tooltip, ResponsiveContainer, Label
} from 'recharts';
import { 
  TrendingUp, 
  ArrowUpCircle, 
  ArrowDownCircle,
  DollarSign,
  Activity,
  BarChart3,
  Clock
} from 'lucide-react';
import { useLocation } from 'react-router-dom';

function BotHistoryChart(props) {
  const { 
    botHistoryData,
    botHistoryDataPnl,
    botHistoryDataPnlPercentage,
    botHistoryGraphData,
    botHistorySorting,
    selectedToken,
    allBotHistoryData
  } = props;
  
  const location = useLocation();
  const botName = location.state?.name;
  const [loading, setLoading] = useState(true);
  const [totalPnl, setTotalPnl] = useState(0);
  const [avgPnlPercent, setAvgPnlPercent] = useState(0);
  const [chartData, setChartData] = useState([]);

  const isAllTokensSelected = selectedToken === 'All';

  useEffect(() => {
    if (!botHistoryData || botHistoryData.length === 0) {
      setTotalPnl(0);
      setAvgPnlPercent(0);
      setLoading(false);
    } else {
      if (isAllTokensSelected) {
        const pnlValue = botHistoryDataPnl?.pnl || 0;
        const avgPnlPercentageValue = botHistoryDataPnlPercentage?.avg_pnl_percentage || 0;
        
        setTotalPnl(pnlValue);
        setAvgPnlPercent(avgPnlPercentageValue);
      } else {
        const totalPnlValue = botHistoryData.reduce(
          (acc, item) => acc + item.profit_loss, 0);
        const totalPnlPercentValue = botHistoryData.reduce(
          (acc, item) => acc + item.profit_loss_percentage, 0);
        const avgPnlPercentValue = totalPnlPercentValue / botHistoryData.length;

        setTotalPnl(totalPnlValue);
        setAvgPnlPercent(avgPnlPercentValue);
      }
      setLoading(false);
    }
  }, [botHistoryData, botHistoryDataPnl, botHistoryDataPnlPercentage, isAllTokensSelected, selectedToken]);

  useEffect(() => {
    if (!botHistoryGraphData || botHistoryGraphData.length === 0) {
      setChartData([]);
      return;
    }

    const formattedData = botHistoryGraphData.map((item, index) => ({
      time: item.sell_time,
      price: item.sell_price,
      rawDate: new Date(item.sell_time),
      index: index
    }));

    const sortedData = formattedData.sort((a, b) => a.rawDate - b.rawDate);

    setChartData(sortedData);
  }, [botHistoryGraphData]);

  const gradientOffset = () => {
    if (chartData.length === 0) return 0.5;
    const dataMax = Math.max(...chartData.map((i) => i.price));
    const dataMin = Math.min(...chartData.map((i) => i.price));

    if (dataMax <= 0) return 0;
    if (dataMin >= 0) return 1;
    return dataMax / (dataMax - dataMin);
  };

  const off = gradientOffset();
  
  const getDisplayValue = () => {
    if (isAllTokensSelected) {
      const pnlValue = botHistoryDataPnl?.pnl || 0;
      return pnlValue;
    } else {
      return totalPnl;
    }
  };

  const displayValue = getDisplayValue();
  const isPositive = displayValue >= 0;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const date = new Date(data.time);
      const formattedDate = date.toLocaleDateString('en-US', { 
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
      const formattedTime = date.toLocaleTimeString('en-US', { 
        hour: '2-digit',
        minute: '2-digit'
      });
      
      return (
        <div className="modern-chart-tooltip">
          <div className="tooltip-header">
            <Clock size={14} />
            <span>{formattedDate} {formattedTime}</span>
          </div>
          <div className="tooltip-content">
            <div className="tooltip-row">
              <span className="tooltip-label">Profit: </span>
              <span className={`tooltip-value ${data.price >= 0 ? 'positive' : 'negative'}`}>
                ${data.price.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="modern-bot-history-chart">
      {/* Modern Header */}
      <div className="chart-header-section">
        <div className="header-content">
          <div className="header-left">
            <div className="bot-title">
              <Activity size={24} className="title-icon" />
              <h2 className="chart-title">{botName || 'Bot Performance'}</h2>
            </div>
            <p className="chart-subtitle">Real-time trading performance analytics</p>
          </div>
          
          <div className="header-stats">
            <div className="stat-card">
              <div className="stat-icon">
                {isPositive ? 
                  <ArrowUpCircle size={24} className="profit-icon" /> : 
                  <ArrowDownCircle size={24} className="loss-icon" />
                }
              </div>
              <div className="stat-content">
                <span className="stat-label">Total P&L</span>
                <span className={`stat-value ${isPositive ? 'positive' : 'negative'}`}>
                  ${displayValue.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Controls */}
      <div className="chart-controls">
        <div className="controls-left">
          <div className="metric-info">
            <span className="control-label">
              <BarChart3 size={16} />
              Showing:
            </span>
            <div className="metric-display">
              <DollarSign size={16} />
              <span>Profit Timeline</span>
            </div>
          </div>
        </div>
        
        <div className="controls-right">
          <div className="chart-info">
            <span className="control-label">
              <Activity size={16} />
              Chart Data:
            </span>
            <span className="data-count">
              {chartData.length} {chartData.length === 1 ? 'Trade' : 'Trades'}
            </span>
          </div>
        </div>
      </div>

      {/* Modern Chart Container */}
      <div className="modern-chart-container">
        {loading ? (
          <div className="chart-loading">
            <div className="loading-spinner">
              <TrendingUp size={48} className="spinner-icon" />
            </div>
            <p className="loading-text">Loading chart data...</p>
          </div>
        ) : chartData.length === 0 ? (
          <div className="chart-empty-state">
            <BarChart3 size={64} className="empty-icon" />
            <h3>No Data Available</h3>
            <p>No trading data found for the selected period</p>
          </div>
        ) : (
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                <XAxis
                  dataKey="time"
                  tick={({ x, y, payload }) => {
                    const date = new Date(payload.value);
                    const formattedDate = date.toLocaleDateString('en-US', { 
                      day: 'numeric',
                      month: 'short'
                    });
                    const formattedTime = date.toLocaleTimeString('en-US', { 
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                    
                    return (
                      <g transform={`translate(${x},${y + 10})`}>
                        <text x={0} y={0} dy={16} textAnchor="middle" fill="#6b7280" fontSize="12">
                          <tspan x="0" dy="0">{formattedDate}</tspan>
                          <tspan x="0" dy="15">{formattedTime}</tspan>
                        </text>
                      </g>
                    );
                  }}
                >
                  <Label
                    value="Trading Timeline"
                    position="insideBottom"
                    offset={-40}
                    style={{ textAnchor: 'middle', fontWeight: '600', fill: '#374151', fontSize: '14px' }}
                  />
                </XAxis>
                <YAxis tick={{ fontSize: 12, fill: '#6b7280' }}>
                  <Label
                    value="Profit ($)"
                    angle={-90}
                    offset={-5}
                    position="insideLeft"
                    style={{ textAnchor: 'middle', fontWeight: '600', fill: '#374151', fontSize: '14px' }}
                  />
                </YAxis>
                <Tooltip content={<CustomTooltip />} />
                <defs>
                  <linearGradient id="modernSplitColor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset={off} stopColor="#10b981" stopOpacity={0.8} />
                    <stop offset={off} stopColor="#ef4444" stopOpacity={0.8} />
                  </linearGradient>
                  <linearGradient id="modernAreaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="url(#modernSplitColor)" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="url(#modernSplitColor)" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="price"
                  stroke="url(#modernSplitColor)"
                  fill="url(#modernAreaGradient)"
                  strokeWidth={3}
                  activeDot={{ 
                    r: 8, 
                    strokeWidth: 3, 
                    stroke: '#fff',
                    fill: isPositive ? '#10b981' : '#ef4444',
                    filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.15))'
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    botHistoryDataPnl: state.botReducer.botHistoryDataPnl,
    botHistoryDataPnlPercentage: state.botReducer.botHistoryDataPnlPercentage,
    botHistoryGraphData: state.botReducer.botHistoryGraphData,
    botHistorySorting: state.botReducer.botHistorySorting,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BotHistoryChart);